import {hasUserConsents} from '@s-ui/consents'
import cookie from '@s-ui/js/lib/cookie'
import {nanoid} from '@s-ui/js/lib/nanoid'
import {PDE} from '@s-ui/pde'
import DefaultAdapter from '@s-ui/pde/lib/adapters/default'

import {SEGMENT_ANONYMOUS_COOKIE_KEY, SEGMENT_USER_ID_COOKIE_KEY} from '../config/index.js'
import {getDeviceStats} from '../helpers/getDeviceStats.js'
import {cleanExtraQuotes} from '../helpers/index.js'

const NODE_ENV = process.env.STAGE || process.env.NODE_ENV || 'production'
const OPTIMIZELY_PROD_API_KEY = 'Lk6APazfgXGoKa8ZpyeLQ'
const OPTIMIZELY_DEV_API_KEY = 'JDzB2fFPPSy416LNWaxaj'
const ONE_MINUTE_MILLIS = 60 * 1000
const REQUIRED_CONSENTS = [1, 6, 8, 10]
const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
}
export const QA_TEST_REGEX = /qa_test=1/i

let OptimizelyAdapter
let optimizelyInstance

const getDeviceType = ({userAgent}) => {
  const {isMobile, isTablet} = getDeviceStats(userAgent)
  const isDesktop = !isMobile && !isTablet
  let deviceType

  switch (true) {
    case isMobile:
      deviceType = DEVICE_TYPES.MOBILE
      break
    case isTablet:
      deviceType = DEVICE_TYPES.TABLET
      break
    case isDesktop:
      deviceType = DEVICE_TYPES.DESKTOP
      break
    default:
      break
  }
  return deviceType
}

const checkQATestParams = ({search}) => {
  return QA_TEST_REGEX.test(search)
}

// The client decides every flag again against its own datafile, and that download does not always
// land (ad blockers routinely block cdn.optimizely.com), in which case every flag reads as
// disabled. A flag that drives markup then hydrates into DOM the server never sent. Recording what
// SSR decided lets the hydration render reuse it (see helpers/pde.js); hydrating the datafile
// itself — the mechanism @s-ui/pde offers through `window.__INITIAL_CONTEXT_VALUE__.pde` — would
// add ~650KB to every response.
const recordServerDecisions = ({adapter}) => {
  const decisions = {}
  const decide = adapter.decide.bind(adapter)

  adapter.decide = params => {
    const decision = decide(params)
    // Only the plain value fields: a decision also carries the `userContext` that produced it,
    // which holds the SDK client and is not serializable.
    const {enabled, flagKey, ruleKey, variables, variationKey} = decision ?? {}

    if (flagKey) decisions[flagKey] = {enabled, flagKey, ruleKey, variables, variationKey}

    return decision
  }

  return decisions
}

export const setupOptimizelyAdapter = async ({isClient}) => {
  if (optimizelyInstance) return optimizelyInstance

  try {
    OptimizelyAdapter = await import('@s-ui/pde/lib/adapters/optimizely').then(module => module.default)
    const commonDatafileOptions = {
      autoUpdate: !isClient,
      updateInterval: ONE_MINUTE_MILLIS // every minute updated instance's datafile
    }
    optimizelyInstance = OptimizelyAdapter.createOptimizelyInstance({
      sdkKey: NODE_ENV === 'production' ? OPTIMIZELY_PROD_API_KEY : OPTIMIZELY_DEV_API_KEY,
      options: {
        ...commonDatafileOptions
      }
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

const getClientUserId = () => {
  const anonymousUserId = cookie.get(SEGMENT_ANONYMOUS_COOKIE_KEY)
  const segmentUserId = cookie.get(SEGMENT_USER_ID_COOKIE_KEY)

  // return nanoid if on SPA and no cookie is found (this should only happen on dev / local env)
  return {
    anonymousUserId: anonymousUserId ? cleanExtraQuotes(anonymousUserId) : nanoid(),
    segmentUserId: cleanExtraQuotes(segmentUserId)
  }
}

const initializeAdapter = async ({cookies, deviceType, domain, hydratedUserId, isClient, isQATest, req, site}) => {
  const {anonymousUserId: cookieAnonymousUserId, segmentUserId} = isClient && getClientUserId()
  // Prefer the userId the server already resolved for this request (hydrated from SSR)
  // over recomputing it from cookies on the client: cookie parsing (quoting/encoding) can
  // legitimately differ between server (cookie-parser) and client (@s-ui/js cookie.get),
  // and a first-time visitor gets a fresh id on each side otherwise. Either divergence
  // makes Optimizely bucket the same visitor into a different variant on SSR vs CSR.
  const anonymousUserId = isClient ? hydratedUserId || cookieAnonymousUserId : undefined

  // Force Segment to use the already anonymous ID created from server.
  if (anonymousUserId) window.analytics.setAnonymousId(anonymousUserId)

  // const [userTraitsError, userTraits] = await domain.get('get_user_traits_tracking_use_case').execute()

  if (OptimizelyAdapter && optimizelyInstance) {
    const optimizelyUserId = anonymousUserId || req.pdeUserId

    const pdeOptions = {
      userId: optimizelyUserId,
      hasUserConsents: hasUserConsents({
        requiredConsents: REQUIRED_CONSENTS,
        cookies
      }),
      applicationAttributes: {
        anonymous_user_id: optimizelyUserId,
        context_device_type: deviceType,
        context_qa_test: isQATest,
        segment_user_id: segmentUserId || req?.segmentUserId || '',
        site
        // ...(!userTraitsError && userTraits.getAttributesWithPrefix())
      }
    }

    return {
      adapter: new OptimizelyAdapter({
        optimizely: optimizelyInstance,
        ...pdeOptions
      }),
      optimizelyUserId
    }
  }

  return {adapter: new DefaultAdapter(), optimizelyUserId: anonymousUserId || req?.pdeUserId || null}
}

export const getExperimentationContext = async ({
  cookies,
  domain,
  hydratedUserId,
  isClient,
  isMotos,
  req,
  search,
  userAgent
}) => {
  const site = isMotos ? 'motos.coches.net' : 'coches.net'
  const deviceType = getDeviceType({userAgent})
  const isQATest = checkQATestParams({search})
  const {adapter, optimizelyUserId} = await initializeAdapter({
    cookies,
    deviceType,
    domain,
    hydratedUserId,
    isClient,
    isQATest,
    req,
    site
  })

  await adapter.onReady()

  // Filled while the page renders, read back when the hydration payload is serialized
  // (see entry.server.tsx `onAllReady`).
  const pdeDecisions = isClient ? undefined : recordServerDecisions({adapter})

  return {
    pde: new PDE({
      adapter,
      hasUserConsents: hasUserConsents({
        requiredConsents: REQUIRED_CONSENTS,
        cookies
      })
    }),
    // Plain, serializable copy of the userId used for bucketing so it can be hydrated
    // from server to client (see entry.server.tsx / entry.client.tsx). `pde` itself is
    // excluded from hydration because its adapter holds functions/SDK instances.
    pdeUserId: optimizelyUserId,
    pdeDecisions
  }
}
